import type {
    AnatomySampleMap,
    DocsTranslate
} from "../types";
import {
    ColorSwatchPicker,
    MarkdownEditor,
    DateTimePicker,
    TextAreaInput,
    MonthSelector,
    Questionnaire,
    YearSelector,
    DateSelector,
    TimeSelector,
    NumericInput,
    RadioButton,
    ColorSlider,
    InputGroup,
    FileUpload,
    ColorField,
    CodeEditor,
    TextInput,
    SelectBox,
    Combobox,
    InputOTP,
    CheckBox,
    Slider
} from "ncore-ui-kit";
import {
    shapeConfigs
} from "../sampleTokens";
import {
    SearchIcon
} from "../icons";

const SAMPLE_DATE = new Date(2026, 9, 14);

const SAMPLE_TIME = new Date(2026, 9, 14, 14, 32);

const SAMPLE_FILE_SIZE = 1.2 * 1024 * 1024;

const renderCheckBox = (t: DocsTranslate, isChecked: boolean) => <CheckBox
    subTitle={t("sample.hint")}
    onPress={() => undefined}
    title={t("sample.label")}
    isChecked={isChecked}
/>;

const renderRadioButton = (t: DocsTranslate, isChecked: boolean) => <RadioButton
    subTitle={t("sample.hint")}
    onPress={() => undefined}
    title={t("sample.label")}
    isChecked={isChecked}
/>;

const SAMPLE_COLOR = "#006155";

const renderColorSlider = (t: DocsTranslate, value: number) => <ColorSlider
    title={t("sample.label")}
    color={SAMPLE_COLOR}
    initialValue={value}
    channel="hue"
/>;

const renderSlider = (t: DocsTranslate, value: number) => <Slider
    hintText={t("sample.hint")}
    title={t("sample.label")}
    defaultValue={value}
/>;

export const formSamples: AnatomySampleMap = {
    Questionnaire: (t) => ({
        node: <Questionnaire
            items={[
                {
                    choices: [
                        {
                            title: t("sample.option1"),
                            value: "option1"
                        },
                        {
                            title: t("sample.option2"),
                            value: "option2"
                        },
                        {
                            title: t("sample.option3"),
                            value: "option3"
                        }
                    ],
                    description: t("sample.hint"),
                    title: t("sample.question"),
                    name: "question"
                },
                {
                    title: t("sample.step2"),
                    name: "step2"
                },
                {
                    title: t("sample.step3"),
                    name: "step3"
                }
            ]}
            title={t("sample.title")}
        />,
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    SelectBox: (t) => ({
        node: <SelectBox
            titleExtractor={(item) => item.__title}
            data={[
                {
                    __title: t("sample.option1"),
                    __key: "option1"
                },
                {
                    __title: t("sample.option2"),
                    __key: "option2"
                },
                {
                    __title: t("sample.option3"),
                    __key: "option3"
                }
            ]}
            keyExtractor={(item) => item.__key}
            hintText={t("sample.hint")}
            title={t("sample.label")}
            spreadBehaviour="stretch"
        />,
        width: shapeConfigs.drawerWidth
    }),
    CheckBox: (t) => ({
        states: [
            {
                label: t("sample.stateUnchecked"),
                node: renderCheckBox(t, false)
            },
            {
                label: t("sample.stateChecked"),
                node: renderCheckBox(t, true)
            }
        ]
    }),
    DateSelector: () => ({
        node: <DateSelector
            selectMultipleObject={() => undefined}
            selectObject={() => undefined}
            viewDate={SAMPLE_DATE}
            date={SAMPLE_DATE}
            variant="single"
        />,
        maxScale: 2
    }),
    MonthSelector: () => ({
        node: <MonthSelector
            selectMultipleObject={() => undefined}
            selectObject={() => undefined}
            viewDate={SAMPLE_DATE}
            date={SAMPLE_DATE}
            variant="single"
        />,
        maxScale: 2
    }),
    RadioButton: (t) => ({
        states: [
            {
                node: renderRadioButton(t, false),
                label: t("sample.stateUnchecked")
            },
            {
                node: renderRadioButton(t, true),
                label: t("sample.stateChecked")
            }
        ]
    }),
    TimeSelector: () => ({
        node: <TimeSelector
            selectMultipleObject={() => undefined}
            isWorkWith24HoursFormat={false}
            selectObject={() => undefined}
            date={SAMPLE_TIME}
            pickerType="time"
            variant="single"
        />
    }),
    YearSelector: () => ({
        node: <YearSelector
            selectMultipleObject={() => undefined}
            selectObject={() => undefined}
            viewDate={SAMPLE_DATE}
            date={SAMPLE_DATE}
            variant="single"
        />,
        maxScale: 2
    }),
    TextAreaInput: (t) => ({
        node: <TextAreaInput
            placeholder={t("sample.placeholder")}
            hintText={t("sample.hint")}
            title={t("sample.label")}
            spreadBehaviour="stretch"
            maxLength={200}
        />,
        width: shapeConfigs.drawerWidth
    }),
    TextInput: (t) => ({
        node: <TextInput
            placeholder={t("sample.placeholder")}
            hintText={t("sample.hint")}
            title={t("sample.label")}
            spreadBehaviour="stretch"
        />,
        width: shapeConfigs.drawerWidth
    }),
    ColorSlider: (t) => ({
        states: [
            {
                label: t("sample.stateDefault"),
                node: renderColorSlider(t, 181)
            },
            {
                label: t("sample.stateActive"),
                node: renderColorSlider(t, 264)
            }
        ],
        width: shapeConfigs.drawerWidth
    }),
    InputGroup: (t) => ({
        node: <InputGroup
            hintText={t("sample.hint")}
            title={t("sample.label")}
            spreadBehaviour="stretch"
        >
            <InputGroup.Addon
                icon={SearchIcon}
            />
            <InputGroup.Input
                placeholder={t("sample.search")}
                aria-label={t("sample.label")}
            />
            <InputGroup.Addon
                align="end"
            >
                <InputGroup.Button
                    title={t("sample.action")}
                    onPress={() => undefined}
                />
            </InputGroup.Addon>
        </InputGroup>,
        width: shapeConfigs.drawerWidth
    }),
    Slider: (t) => ({
        states: [
            {
                label: t("sample.stateDefault"),
                node: renderSlider(t, 25)
            },
            {
                label: t("sample.stateActive"),
                node: renderSlider(t, 75)
            }
        ],
        width: shapeConfigs.drawerWidth
    }),
    ColorSwatchPicker: (t) => ({
        node: <ColorSwatchPicker
            items={[
                {
                    title: t("sample.option1"),
                    color: "#006155"
                },
                {
                    title: t("sample.option2"),
                    color: "#5180e7"
                },
                {
                    title: t("sample.option3"),
                    color: "#be3600"
                },
                {
                    title: t("sample.label"),
                    color: "#826200"
                }
            ]}
            initialValue="#5180e7"
            isAddEnabled={true}
        />,
        width: shapeConfigs.drawerWidth
    }),
    FileUpload: (t) => ({
        node: <FileUpload
            files={[
                {
                    name: t("sample.fileName"),
                    size: SAMPLE_FILE_SIZE,
                    status: "done",
                    key: "file"
                }
            ]}
            title={t("sample.label")}
            isMultiple={true}
        />,
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    MarkdownEditor: (t) => ({
        node: <MarkdownEditor
            initialValue={t("sample.markdown")}
            defaultMode="write"
        />,
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    CodeEditor: (t) => ({
        width: shapeConfigs.dialogMaxWidth,
        node: <CodeEditor
            value={t("sample.code")}
            isToolbarVisible={true}
            isShowStatusBar={true}
            language="typescript"
        />,
        maxScale: 2
    }),
    Combobox: (t) => ({
        node: <Combobox
            titleExtractor={(item) => item}
            keyExtractor={(item) => item}
            hintText={t("sample.hint")}
            title={t("sample.label")}
            spreadBehaviour="stretch"
            data={[
                t("sample.option1"),
                t("sample.option2"),
                t("sample.option3")
            ]}
        />,
        width: shapeConfigs.drawerWidth
    }),
    ColorField: (t) => ({
        node: <ColorField
            initialValue={SAMPLE_COLOR}
            hintText={t("sample.hint")}
            title={t("sample.label")}
            spreadBehaviour="stretch"
        />,
        width: shapeConfigs.drawerWidth
    }),
    DateTimePicker: (t) => ({
        node: <DateTimePicker
            hintText={t("sample.hint")}
            title={t("sample.label")}
            spreadBehaviour="stretch"
            initialDate={SAMPLE_DATE}
            pickerType="date"
        />,
        width: shapeConfigs.drawerWidth
    }),
    InputOTP: (t) => ({
        node: <InputOTP
            hintText={t("sample.hint")}
            title={t("sample.label")}
            initialValue="123"
            groupSize={3}
            length={6}
        />
    }),
    NumericInput: (t) => ({
        node: <NumericInput
            hintText={t("sample.hint")}
            title={t("sample.label")}
            spreadBehaviour="stretch"
            isShowStepper={true}
            placeholder="0"
        />,
        width: shapeConfigs.drawerWidth
    })
};
